import React, { ChangeEvent } from 'react';

import IconCheckRounded from '@/components/shared/form/checkbox/check-rounded';
import { FieldGroup } from '@/components/shared/form/field-group';
import { Label } from '@/components/shared/form/label';
import { Flex } from '@/components/shared/layout/flex';
import { styled } from '@/stitches.config';
import { IThemeLCV } from '@/types/shared';
import { ITracking } from '@/types/tracking';

const CheckboxGroup = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  columnGap: '$space-6',
  variants: {
    disabled: {
      true: {
        pe: 'none',
      },
    },
    direction: {
      row: {
        flexDirection: 'row',
      },
      column: {
        flexDirection: 'column',
      },
    },
  },
  defaultVariants: {
    direction: 'row',
  },
});

const CheckboxItem = styled(Flex, {
  position: 'relative',
  my: '$space-3',
});

const CheckboxIcon = styled('i', {
  size: '$size-6',
  minWidth: '$size-6',
  color: '$white',
  position: 'absolute',
  left: '$space-0',
  top: '$space-0',
  pe: 'none',
  zIndex: '1',
  fontSize: '0',
  fontStyle: 'normal',
  lineHeight: '0',
  display: 'inline-block',
  verticalAlign: 'middle',
  opacity: '0',
  transitionProperty: 'opacity',
  transitionDelay: 'var(--transition-delay)',
  transitionDuration: 'var(--transition-duration)',
  transitionTimingFunction: 'var(--transition-easing)',
  '& svg': {
    width: '100%',
    height: 'auto',
    color: '$current',
    transitionProperty: 'color',
    transitionDelay: 'var(--transition-delay)',
    transitionDuration: 'var(--transition-duration)',
    transitionTimingFunction: 'var(--transition-easing)',
    '& path, & polygon': {
      fill: 'currentColor',
    },
  },
});

const StyledCheckbox = styled('input', {
  appearance: 'none',
  border: 0,
  position: 'relative',
  size: '$size-6',
  flexBasis: '$size-6',
  flexGrow: 0,
  flexShrink: 0,
  maxWidth: '$size-6',
  cursor: 'pointer',
  '&::before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    left: '$space-0',
    top: '$space-0',
    size: '$size-6',
    br: '$rounded-1',
    bc: 'var(--input-background-color)',
    bs: 'inset 0px 0px 0px 2px var(--input-stoke-color)',
    zIndex: '1',
    pe: 'none',
    '--transition-duration': '0.25s',
    '--transition-easing': 'ease',
    transitionProperty: 'box-shadow, background-color',
    transitionDelay: 'var(--transition-delay)',
    transitionDuration: 'var(--transition-duration)',
    transitionTimingFunction: 'var(--transition-easing)',
  },
  '& ~ label': {
    position: 'relative',
    cursor: 'pointer',
    us: 'none',
    paddingLeft: '$space-2',
  },
  '&:checked': {
    '&::before': {
      bc: 'var(--input-checkbox-color-checked)',
      bs: 'inset 0px 0px 0px 2px var(--input-checkbox-color-checked)',
    },
    '& ~ i': {
      opacity: '1',
    },
  },
  '&:hover': {
    '&::before': {
      bs: 'inset 0px 0px 0px 2px var(--input-checkbox-color-hover)',
    },
  },
  '&:disabled': {
    pe: 'none',
    '&::before': {
      bc: 'var(--input-background-color-disabled)',
      bs: 'inset 0px 0px 0px 2px var(--input-checkbox-color-disabled)',
    },
    '&:checked': {
      '&::before': {
        bc: 'var(--input-checkbox-color-disabled)',
      },
      '& ~ i': {
        color: 'var(--input-background-color-disabled)',
      },
    },
    '& ~ label': {
      '& *': {
        color: 'var(--input-checkbox-color-disabled)',
      },
    },
  },
  variants: {
    theme: {
      light: {
        '& ~ label': {
          '& > *': {
            color: 'var(--input-color)',
          },
        },
      },
      dark: {
        '&::before': {
          bc: 'var(--input-background-color-dark)',
        },
        [`& ~ ${CheckboxIcon}`]: {
          color: '$white',
        },
        '& ~ label': {
          '& > *': {
            color: '$white',
          },
        },
      },
    },
    error: {
      true: {
        '&::before': {
          bs: 'inset 0px 0px 0px 2px var(--input-stoke-color-error)',
        },
        '& ~ label': {
          '& *': {
            color: 'var(--input-color-error)',
          },
        },
      },
    },
  },
  defaultVariants: {
    theme: 'light',
  },
});

interface CheckboxItemProps {
  children?: React.ReactNode;
  name: string;
  label?: string;
  checked?: boolean;
  tracking?: ITracking;
}

export interface CheckboxProps
  extends React.ComponentProps<typeof CheckboxGroup> {
  variant?: 'black' | 'white';
  direction?: 'row' | 'column';
  disabled?: boolean;
  items: CheckboxItemProps[];
  onChangeInput?: (name: string, value: boolean) => void;
  label?: string;
  secondaryLabel?: string;
  required?: boolean;
  error?: boolean;
  errorMessage?: string;
  message?: string;
  css?: any;
  className?: string;
  name: string;
  theme?: IThemeLCV;
}

export const Checkbox: React.FC<CheckboxProps> = ({
  direction = 'row',
  disabled = false,
  items,
  onChangeInput,
  label,
  secondaryLabel,
  required = false,
  error = false,
  errorMessage,
  message,
  css,
  className,
  ...props
}: CheckboxProps) => {
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChangeInput && onChangeInput(e.target.name, e.target.checked);
  };

  return (
    <FieldGroup
      htmlFor={props?.name}
      label={label}
      secondaryLabel={secondaryLabel}
      required={required}
      error={error}
      errorMessage={errorMessage}
      message={message}
      css={css}
      className={className}
      test={{
        dataTest: props?.name,
      }}
      theme={props.theme}
    >
      <CheckboxGroup direction={direction} disabled={disabled}>
        {items.map((item, index) => {
          return (
            <CheckboxItem className="checkbox-item" key={index}>
              <StyledCheckbox
                id={item?.name}
                name={item?.name}
                type="checkbox"
                checked={item?.checked}
                disabled={disabled}
                error={error}
                onChange={handleChange}
                onClick={(ev) => ev.stopPropagation()}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                theme={props.theme}
                data-track={item?.tracking?.dataTrack}
                data-track-value={item?.tracking?.dataTrackValue}
                data-track-text={item?.tracking?.dataTrackText}
                data-track-url={item?.tracking?.dataTrackUrl}
                data-test={`input_${item?.name}`}
              />
              <CheckboxIcon>
                <IconCheckRounded />
              </CheckboxIcon>
              {!!(item.label || item?.children) && (
                <Label
                  htmlFor={item?.name}
                  label={item?.label}
                  test={{
                    dataTest: item?.name,
                  }}
                >
                  {item?.children}
                </Label>
              )}
            </CheckboxItem>
          );
        })}
      </CheckboxGroup>
    </FieldGroup>
  );
};
Checkbox.displayName = 'Checkbox';
